import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, LayoutDashboard, FlaskConical, Users, LogOut, BarChart3, Coffee } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const roleColor = {
  admin: "text-status-critical border-status-critical/40 bg-status-critical/10",
  instructor: "text-status-warning border-status-warning/40 bg-status-warning/10",
  trainee: "text-primary border-primary/40 bg-primary/10",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, show: true },
    { to: "/scenarios", label: "Lab Library", icon: FlaskConical, show: true },
    { to: "/instructor", label: "Instructor", icon: Users, show: ["instructor", "admin"].includes(user?.role) },
    { to: "/admin", label: "Admin", icon: BarChart3, show: user?.role === "admin" },
  ];

  return (
    <div className="min-h-screen bg-background cr-grid">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/90 backdrop-blur-md">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <button onClick={() => navigate("/dashboard")} data-testid="brand-home" className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-sm bg-primary/15 border border-primary/40 flex items-center justify-center">
                <ShieldAlert className="w-4 h-4 text-primary" />
              </div>
              <span className="font-heading font-extrabold tracking-tight text-sm">CONTROL ROOM SIM</span>
            </button>
            <nav className="hidden md:flex items-center gap-1">
              {nav.filter((n) => n.show).map((n) => {
                const active = location.pathname === n.to;
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                      active ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    }`}
                  >
                    <n.icon className="w-4 h-4" /> {n.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://buymeacoffee.com/xbow"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="support-link"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold border border-status-warning/40 bg-status-warning/10 text-status-warning hover:bg-status-warning/20 transition-colors"
            >
              <Coffee className="w-3.5 h-3.5" /> Support
            </a>
            {user && (
              <span className={`hidden sm:inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border ${roleColor[user.role]}`}>
                {user.role}
              </span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="outline-none">
                <Avatar className="w-8 h-8 border border-border">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-secondary text-xs">{user?.name?.[0] ?? "U"}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover border-border">
                <DropdownMenuLabel className="font-normal">
                  <div className="font-medium text-sm">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-status-critical focus:text-status-critical cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">{children}</main>
    </div>
  );
}
